<script setup>
import { ref, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import Loading from "@/components/Loading";
import { changeEnvironment } from "@/api/auth";
import Swal from "sweetalert2";
import { useBranding } from "@/utils/branding";

// const searchQuery = ref("");
const showDropdown = ref(false);
const isLoading = ref(false);

const router = useRouter();
const store = useStore();
const route = useRoute();

// Get branding information
const { brandLogo, brandName } = useBranding();

// Get environment from Vuex store
const environment = computed(() => store.getters.currentEnvironment);

const isDetailCompanyPage = computed(() => route.name === "DetailCompany");
const isDocumentCompanyPage = computed(() => route.name === "DocumentCompany");
const isBankCompanyPage = computed(() => route.name === "BankCompany");

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
};

const handleLogout = async () => {
  isLoading.value = true;
  try {
    const result = await store.dispatch("auth/logout");
    if (result.success) {
      router.push({ name: "Signin" });
    }
  } catch (error) {
    console.error('Error during logout:', error);
    // Fallback to manual cleanup if logout action fails
    localStorage.removeItem("auth_token");
    localStorage.removeItem("merchantEmail");
    localStorage.removeItem("merchant_user_email");
    localStorage.removeItem("merchantId");
    localStorage.removeItem("merchantKyc");
    localStorage.removeItem("merchantName");
    localStorage.removeItem("merchant_email");
    localStorage.removeItem("merchantAppId");
    localStorage.removeItem("merchantAppSecret");
    localStorage.removeItem("merchantStatusKyb");
    router.push({ name: "Signin" });
  } finally {
    isLoading.value = false;
  }
};

const handleClose = () => {
  router.push({ name: "Home" });
};

const setEnvironment = async (env) => {
  // Validate KYB status before allowing production environment switch
  if (env === 'production') {
    const merchantStatusKyb = localStorage.getItem("merchantStatusKyb");
    
    if (merchantStatusKyb !== 'approved') {
      await Swal.fire({
        title: 'Access Denied',
        text: 'You cannot use the Production environment yet. Please submit your company data first and wait for your KYB status to be approved.',
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6'
      });
      return; // Stop execution if KYB is not approved
    }
  }

  try {
    const formData = new FormData();
    formData.append("environment", env);
    
    const response = await changeEnvironment(formData);
    
    if (response.status === "success") {
      // Update store after successful API call
      store.dispatch("changeEnvironment", env);
      
      // Set app_id and app_secret from API response
      if (response.data && response.data.app_id && response.data.app_secret) {
  
        localStorage.setItem("merchantAppId", response.data.app_id);
        localStorage.setItem("merchantAppSecret", response.data.app_secret);
        
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('environmentChanged', {
          detail: {
            environment: env,
            appId: response.data.app_id,
            appSecret: response.data.app_secret
          }
        }));

        // refresh page
        window.location.reload();
      } else {
        console.warn("No app_id or app_secret in response data:", response.data);
      }
    } else {
      console.error("Failed to change environment:", response.message);
    }
  } catch (error) {
    console.error("Error changing environment:", error);
  }
};

// Get user email from localStorage and extract first letter
const getUserInitial = () => {
  const userEmail =
    localStorage.getItem("merchant_user_email") ||
    localStorage.getItem("merchantEmail");
  if (userEmail) {
    return userEmail.charAt(0).toUpperCase();
  }
  return "A"; // Fallback to 'A' if no email found
};
</script>

<template>
  <nav class="navbar">
    <div class="container-fluid">
      <div class="navbar-left">
        <router-link class="logo-container" to="/">
          <img :src="brandLogo" :alt="brandName" />
          <span
            v-if="
              isDetailCompanyPage || isDocumentCompanyPage || isBankCompanyPage
            "
            class="page-title"
            >Verified Account</span
          >
        </router-link>

        <!-- Environment Toggle - positioned to align with main content -->
        <div
          v-if="
            !isDetailCompanyPage && !isDocumentCompanyPage && !isBankCompanyPage
          "
          class="environment-toggle-wrapper"
        >
          <div class="environment-toggle">
            <div class="btn-group" role="group">
              <button
                type="button"
                class="btn btn-sm"
                :class="
                  environment === 'sandbox'
                    ? 'btn-warning'
                    : 'btn-outline-warning'
                "
                @click="setEnvironment('sandbox')"
              >
                Sandbox
              </button>
              <button
                type="button"
                class="btn btn-sm"
                :class="
                  environment === 'production'
                    ? 'btn-success'
                    : 'btn-outline-success'
                "
                @click="setEnvironment('production')"
              >
                Production
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="navbar-right">
        <div
          v-if="
            isDetailCompanyPage || isDocumentCompanyPage || isBankCompanyPage
          "
        >
          <span class="text-danger" style="cursor: pointer" @click="handleClose"
            >Close</span
          >
        </div>
        <template v-else>
          <div class="d-flex gap-4" style="align-items: center">
            <!-- <div class="search-container">
              <input
                type="text"
                class="search-input"
                placeholder="Search Transaction ID"
                v-model="searchQuery"
              />
              <i class="fas fa-search search-icon"></i>
            </div> -->

            <!-- <div class="notification-icon">
              <img
                src="./../../../public/assets/icons/ic_notification.svg"
                alt="notification icon"
                width="24px"
                height="24px"
              />
            </div> -->

            <div class="user-profile">
              <div class="avatar" @click.stop="toggleDropdown">
                {{ getUserInitial() }}
              </div>
              <div v-if="showDropdown" class="dropdown-menu">
                <ul>
                  <li v-if="role === 'Admin'">
                    <router-link to="/profile" @click="showDropdown = false"
                      >Profile</router-link
                    >
                  </li>
                  <!-- <li><router-link to="/" @click="showDropdown = false">Settings</router-link></li> -->
                  <li @click="handleLogout">Logout</li>
                </ul>
              </div>
              <i
                :class="
                  showDropdown ? 'fas fa-chevron-up' : 'fas fa-chevron-down'
                "
                class="arrow-icon"
              ></i>
            </div>
          </div>
        </template>
      </div>
    </div>
    <Loading :isLoading="isLoading" />
  </nav>
</template>

<style scoped>
.navbar {
  position: relative;
  overflow: visible;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  padding: 12px 24px;
  background-color: #ffffff;
  border-bottom: 1px solid #e9ecef;
}

.container-fluid {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.navbar-left {
  display: flex;
  align-items: center;
  gap: 24px;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #212529;
}

.navbar-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.search-container {
  position: relative;
}

.search-input {
  padding: 8px 12px 8px 36px;
  border: 1px solid #ced4da;
  border-radius: 20px;
  font-size: 14px;
  width: 240px;
  height: 32px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.environment-toggle-wrapper {
  display: flex;
  align-items: center;
  margin-left: auto;
  padding-left: 40px;
  margin-top: 4px;
}

.environment-toggle {
  display: flex;
  align-items: center;
}

.environment-toggle .btn-group {
  display: flex;
}

.environment-toggle .btn {
  font-size: 12px;
  padding: 6px 14px;
  border-radius: 4px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.environment-toggle .btn:first-child {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.environment-toggle .btn:last-child {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left: 0;
}

.notification-icon {
  display: flex;
  width: 32px;
  height: 32px;
  transform: rotate(-0.001deg);
  padding: 4px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;

  border-radius: 100px;
  background: var(--Background-Light-Grey, #f4f6f6);
}

.user-profile {
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}

.avatar {
  width: 32px;
  height: 32px;
  background-color: #dde0e0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
}

.arrow-icon {
  font-size: 12px;
  color: #6c757d;
}

.logo-container {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.logo-container img {
  height: 40px;
}

.dropdown-menu {
  position: absolute;
  top: 40px;
  right: 0;
  background-color: #ffffff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 150px;
  z-index: 1050;
  display: block;
}

.dropdown-menu ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.dropdown-menu li {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  color: #212529;
}

.dropdown-menu li:hover {
  background-color: #f8f9fa;
}

.dropdown-menu li a {
  text-decoration: none;
  color: inherit;
  display: block;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .navbar-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
  }

  .environment-toggle-wrapper {
    order: 2;
    margin-left: 0;
    padding-left: 0;
    width: 100%;
    justify-content: flex-start;
    margin-top: 8px;
  }

  .logo-container {
    order: 1;
  }

  .navbar-right {
    flex-wrap: wrap;
    gap: 8px;
  }

  .search-container {
    order: 3;
    width: 100%;
    margin-top: 8px;
  }

  .search-input {
    width: 100%;
  }

  .notification-icon {
    order: 1;
  }

  .user-profile {
    order: 2;
  }
}
</style>
